import EmberService, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature, DataCategory, RequestFormType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy } from 'webapp/utils/common';
import { getCompactString } from 'webapp/utils/filter';

import CommonPopupSourceMetricRequestFormComponent from 'webapp/components/common/popup-source-metric-request-form';
import DataManagerPopupSelectedConnectionsComponent from 'webapp/components/data-manager/popup-selected-connections';
import SourceTypeGroupInfoPopupComponent from 'webapp/components/source/popup-source-type-group-info';
import SourceTypeInfoPopupComponent from 'webapp/components/source/popup-source-type-info';
import SourceWrapperForTemplatesPopupComponent from 'webapp/components/source/wrapper-for/popup-templates';
import type AvailableSourceTypeInfoRoute from 'webapp/routes/datamanager/available/source-type-info';

import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type { SourceTypeData } from 'webapp/models-custom/source-type';
import SourceType from 'webapp/models-custom/source-type';

type SourceTypesByKeyOrId = Record<string, SourceType>;
interface ExtraInfo {
	content: string;
	behaviourAndUsage: string;
	notes: string;
	handlingErrors: string;
	techDetails: string;
}
export type ExtraInfoKey = keyof ExtraInfo;
export type SourceTypeExtraInfo = ExtraInfo | undefined;
type SourceTypesExtraInfoByKey = Record<string, SourceTypeExtraInfo>;

interface MetricExtraInfo {
	content: string;
	whatIs: string;
	notes: string;
	techDetails: string;
}
export type MetricExtraInfoKey = keyof MetricExtraInfo;
export type MetrickeyExtraInfo = MetricExtraInfo | undefined;
type MetrickeysExtraInfoByKey = Record<string, MetrickeyExtraInfo>;

export type TrackingOriginNewConnectionInitiated = 'datamanager' | 'wizard' | 'datasets';

export default class ModelManagerSourceTypesService extends EmberService {
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('connect') declare connectService: Service.Connect;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;

	sourceTypeModels: SourceType[] = [];
	private sourceTypeModelsById: SourceTypesByKeyOrId = {};
	private sourceTypeModelsByKey: SourceTypesByKeyOrId = {};
	@tracked sourceTypesExtraInfoByKey: SourceTypesExtraInfoByKey = {};
	@tracked metrickeysExtraInfoByKey: MetrickeysExtraInfoByKey = {};

	requestExtraInfoByKey(key: string) {
		const token = this.currentUserService.metricLibraryExtraInfoToken,
			tail = token ? '&extraInfoToken=' + token : '';
		if (!Object.hasOwnProperty.call(this.sourceTypesExtraInfoByKey, key)) {
			this.sourceTypesExtraInfoByKey[key] = undefined;
			this.requestService
				.api<SourceTypeExtraInfo>(
					'GET',
					`/metric-library/wp-json/metric-library/v1/integrations/extra-info?integrationKey=${key + tail}`,
					null,
					{
						withoutPrefix: true,
						apiUrl: 'https://databox.com',
						publicUrl: true,
					},
				)
				.then((reply) => {
					const cache = this.sourceTypesExtraInfoByKey;
					cache[key] = reply;
					this.sourceTypesExtraInfoByKey = cache;
				})
				.catch(() => {
					const cache = this.sourceTypesExtraInfoByKey;
					delete cache[key];
					this.sourceTypesExtraInfoByKey = cache;
				});
		}
	}

	@cached
	get connectableSourceTypeModels(): SourceType[] {
		return this.sourceTypeModels.filter((item) => item.isConnectable);
	}

	findModelByKey(key: string): SourceType | null {
		return this.sourceTypeModelsByKey[key] || null;
	}

	findModelById(id: number): SourceType | null {
		return this.sourceTypeModelsById[id] || null;
	}

	openWrapperTokenTemplatesModal(sourceType: SourceType, token?: string) {
		if (!token) {
			token = sourceType.latestToken || '';
		}

		const popup = this.popupService.createModal(SourceWrapperForTemplatesPopupComponent, {
			popupClass: 'wrappertokenpopup',
		});

		popup.open({
			sourceType,
			token,
		});

		return popup;
	}

	goToSourceTypeInfo(
		sourceType: SourceType,
		trackingOrigin?: TrackingOriginNewConnectionInitiated,
	) {
		return this.navigationService
			.transitionToRoute('datamanager.available.source-type-info', sourceType.key)
			.then((route: AvailableSourceTypeInfoRoute) => {
				if (trackingOrigin) {
					route.trackingOrigin = trackingOrigin;
				}
			});
	}

	openSourceTypeInfoPopup(sourceType: SourceType, onConnect: (sourceType: SourceType) => void) {
		const props = {
			model: sourceType,
			onConnect,
		};

		const isGroup = sourceType.isGroup,
			component = isGroup ? SourceTypeGroupInfoPopupComponent : SourceTypeInfoPopupComponent;

		let popupClass = `popup-type-source-type-${isGroup ? 'group-' : ''}info`;
		if (isGroup) {
			popupClass += ' title-bigger';
		}

		const popup = this.popupService.createModal(component, {
			popupClass,
			allowStacked: true, // for connect help
		});

		popup.open(props);

		return popup;
	}

	openSourceRequestForm() {
		const popup = this.popupService.createModal(CommonPopupSourceMetricRequestFormComponent, {
			popupClass: 'popup-type-source-metric-request-form',
		});

		popup.open({
			type: RequestFormType.SOURCE,
		});

		return popup;
	}

	requestConnect(sourceType: SourceType, isOnboarding = false): void {
		if (!sourceType) {
			return;
		}

		if (this.currentUserService.isVerified) {
			if (this.connectService.canCurrentUserConnect) {
				if (this.billingService.checkIfCan(BillingFeature.SOURCES, true)) {
					this.connectService.helpConnectSource(sourceType);

					if (isOnboarding) {
						this.analyticsService.event(
							TrackingEvent.ONBOARDING_CONNECT_HELP_CONNECTING,
							{
								datasource: sourceType.name,
							},
						);
					}
				}
			} else {
				this.connectService.openConnectDisabledPopup();
			}
		} else {
			this.connectService.openEmailVerificationInfoPopup();
			if (isOnboarding) {
				this.analyticsService.event(TrackingEvent.REQUEST_ACCESS_FORBIDDEN);
			}
		}
	}

	getSourceTypesList(): FilterOption<string>[] {
		const list: FilterOption<string>[] = [];

		this.connectableSourceTypeModels.forEach((source) => {
			list.push({
				id: source.key,
				id2: source.id!,
				text: source.name,
				img: source.avatar,
				used: 0,
			});
		});

		return list;
	}

	getSourceTypesListWithCount(sourceTypeIds: string[]): FilterOption<string>[] {
		let list: FilterOption<string>[] = this.getSourceTypesList();

		sourceTypeIds.forEach((sourceTypeId) => {
			const item = list.find((item) => item.id === sourceTypeId);
			if (item) {
				item.used = item.used! + 1;
			}
		});

		// Alphabetical sort
		list.sort((a, b) => {
			if (a.text < b.text) {
				return -1;
			}
			if (a.text > b.text) {
				return 1;
			}
			return 0;
		});

		list = list.filter((item) => item.used);

		return list;
	}

	// ----- Metric Library / Available Integrations categories -----
	@cached
	get connectableSourceTypesByCategory() {
		const connectableSourceTypesByCategory: Record<string, SourceType[]> = {}, // by category id
			connectableSourceTypes = this.connectableSourceTypeModels;

		const allCategories = this.getSourceTypeCategories();
		allCategories.forEach((cat) => {
			connectableSourceTypesByCategory[cat.id] = connectableSourceTypes.filter((sourceType) =>
				this.categoryFilterFunction(sourceType, cat.id),
			);
		});

		return connectableSourceTypesByCategory;
	}

	getSourceTypeCategories() {
		let categories: FilterOption<string>[] = [];

		this.sourceTypeModels.forEach((item) => {
			if (!item.isHidden) {
				(item.categories || []).forEach((cat: string) => {
					const categoryId = getCompactString(cat);
					if (!categories.find((item) => item.id === categoryId)) {
						categories.push({
							id: categoryId,
							text: cat,
						});
					}
				});
			}
		});

		categories = sortBy(categories, 'text');
		categories.unshift({ id: 'all', text: 'All' });

		return categories;
	}

	categoryFilterFunction(item: SourceType, selected: number | string) {
		return item.categories.some((categoryName: string) => {
			return getCompactString(categoryName) === selected;
		});
	}

	// ----- new onboarding / empty screens data categories -----

	dataCategoryOptions = [
		{ id: DataCategory.SAAS, title: 'SaaS', image: 'category-saas' },
		{ id: DataCategory.ECOMMERCE, title: 'eCommerce', image: 'category-ecommerce' },
		{ id: DataCategory.MARKETING, title: 'Marketing', image: 'category-marketing' },
		{ id: DataCategory.ADVERTISING, title: 'Paid advertising', image: 'category-advertising' },
		{ id: DataCategory.CRM_SALES, title: 'CRM & sales', image: 'category-crm-sales' },
		{ id: DataCategory.FINANCIALS, title: 'Financials', image: 'category-financials' },
		{ id: DataCategory.SOCIAL_MEDIA, title: 'Social media', image: 'category-social-media' },
		{ id: DataCategory.OTHER, title: 'Other', image: 'category-other' },
	];

	getSourceTypeCategoriesForDataCategory(dataCategory: DataCategory): string[] {
		switch (dataCategory) {
			case DataCategory.SAAS:
				return [
					'Subscriptions and Payments',
					'CRM',
					'Paid Advertising',
					'Marketing Automation',
					'Accounting & Invoicing',
					'Analytics',
					'Social Media',
					'SEO',
					'Customer Support',
					'Spreadsheets',
				];
			case DataCategory.ECOMMERCE:
				return [
					'Ecommerce',
					'Paid Advertising',
					'Email Marketing',
					'Marketing Automation',
					'Social Media',
					'Analytics',
					'Accounting & Invoicing',
					'Spreadsheets',
				];
			case DataCategory.MARKETING:
				return [
					'Marketing Automation',
					'SEO',
					'Email Marketing',
					'Paid Advertising',
					'Analytics',
					'Social Media',
					'Video Marketing',
					'Spreadsheets',
				];
			case DataCategory.ADVERTISING:
				return ['Paid Advertising'];
			case DataCategory.CRM_SALES:
				return ['CRM'];
			case DataCategory.FINANCIALS:
				return ['Accounting & Invoicing', 'Subscriptions and Payments', 'Spreadsheets'];
			case DataCategory.SOCIAL_MEDIA:
				return ['Social Media'];
			case DataCategory.OTHER:
				return [
					'Customer Support',
					'Project Management',
					'Databases',
					'Spreadsheets',
					'Other',
				];
			default:
				return [];
		}
	}

	openSelectedConnectionsPopup(closeOnOverlayClick = true) {
		const popup = this.popupService.createModal(DataManagerPopupSelectedConnectionsComponent, {
			popupClass: 'popup-type-data-source-selected-connections wizard',
			allowStacked: true, // connect popup
			closeOnOverlayClick,
		});

		popup.open();

		return popup;
	}

	// ------ Requests ------

	_sourceTypesRequested = false;
	_sourceTypesRequestPromise: Promise<void> | null = null;
	requestSourceTypes(
		beforeSourceTypeFilter?: () => Promise<void>,
		throwOnFail = false,
	): Promise<void> {
		const variablePrefix = 'sourceTypes';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.api('GET', '/datasource-types/summary-webapp', null, {
					withoutPrefix: true,
					timeout: 2 * 60 * 1000,
				});
			},
			beforeSourceTypeFilter,
			(reply) => {
				this.filterAndSetSourcetypes(reply as SourceTypeData[]);
			},
			throwOnFail,
		);
	}

	private filterAndSetSourcetypes(unfiltered: SourceTypeData[]): void {
		const spaceId = this.accountService.id;

		let filtered = unfiltered;

		if (spaceId) {
			filtered = unfiltered.filter((source) => {
				return !source.enabledForSpaceIds || source.enabledForSpaceIds.includes(spaceId);
			});
		}

		// generic source types
		const sourceTypes = filtered.map((sourceTypeData, index) => {
			const sourceType = new SourceType(sourceTypeData);
			sourceType.popularity = index;
			return sourceType;
		});

		// testing purposes - partner integration
		// const testPartnerSourceType = new SourceType({id: null, key: 'TestIntegration', name: 'Test Integrations', description: 'some description here', isNative: false, avatar: 'https://cdn1.databox.com/images/apps/s/GitHub.png', avatarBig: 'https://cdn1.databox.com/images/apps/m/GitHub.png', categories: ['Connectors & Software Development'], articleUrls: [] });
		// sourceTypes.push(testPartnerSourceType);

		this.sourceTypeModels = sourceTypes;

		// optimized lists for fast access
		const sourceTypesById: SourceTypesByKeyOrId = {},
			sourceTypesByKey: SourceTypesByKeyOrId = {};
		sourceTypes.forEach((sourceType) => {
			if (typeof sourceType.id === 'number') {
				sourceTypesById[sourceType.id] = sourceType;
			}
			sourceTypesByKey[sourceType.key] = sourceType;
		});
		this.sourceTypeModelsById = sourceTypesById; // don't use this with id 0 !!!
		this.sourceTypeModelsByKey = sourceTypesByKey;
	}

	requestMetricExtraInfoByKey(integrationKey: string, shortMetricKey: string) {
		const key = `${integrationKey}@${shortMetricKey}`,
			token = this.currentUserService.metricLibraryExtraInfoToken,
			tail = token ? '&extraInfoToken=' + token : '';

		if (!Object.hasOwnProperty.call(this.metrickeysExtraInfoByKey, key)) {
			this.metrickeysExtraInfoByKey[key] = undefined;
			return this.requestService
				.api<MetricExtraInfo>(
					'GET',
					`/metric-library/wp-json/metric-library/v1/metrics/extra-info?integrationKey=${integrationKey}&metricKey=${shortMetricKey + tail}`,
					null,
					{
						withoutPrefix: true,
						apiUrl: 'https://databox.com',
						publicUrl: true,
					},
				)
				.then((reply) => {
					const cache = this.metrickeysExtraInfoByKey;
					cache[key] = reply;
					this.metrickeysExtraInfoByKey = cache;
				})
				.catch(() => {
					const cache = this.metrickeysExtraInfoByKey;
					delete cache[key];
					this.metrickeysExtraInfoByKey = cache;
				});
		}

		return Promise.resolve();
	}

	triggerNewConnectionInitiated(
		model: SourceType,
		origin: TrackingOriginNewConnectionInitiated,
	): void {
		this.analyticsService.event(TrackingEvent.NEW_CONNECTION_INITIATED, {
			origin,
			datasource: model.key,
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/source-types': ModelManagerSourceTypesService;
	}
}
